/* ──────────────────────────────────────────────────────────────────────
 * AssignmentHistoryPanel
 *
 * The append-only audit trail of who has held this lead and for how
 * long. One row per assignment ever made — the current owner is
 * highlighted with an "Active" badge; previous owners carry a removed
 * date + duration + their own activity count.
 *
 * Spec layout (the AI Lead-Assignment History block):
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Staff           │ Assigned    │ Removed   │ Duration │ Acts  │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │ • Williams Ketch │ May 10      │ May 19    │ 9 days   │ 6     │
 *   │ • Lesaa Robert   │ May 19      │ Active    │ Active   │ 2     │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Data shape comes from /leads/{id}/assignment-history — eager-loaded
 * with from/to/changed-by users, plus `activity_count` +
 * `duration_seconds` computed server-side from lead_activities.
 *
 * Layout: drops in as a sibling card to the operational stack (Lead
 * Management / Quick Actions / Follow-up Management) in LeadDetailPage's
 * left column. Compact by design — header + table, no charts, no
 * inline forms. Audit data is read-only.
 * ──────────────────────────────────────────────────────────────────── */

import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Stack, Typography, Paper, Avatar, Chip, Alert, CircularProgress,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import {
  fetchAssignmentHistory, type AssignmentHistoryRow,
} from "./leadApi";

const NAVY    = "#1a3353";
const GOLD    = "#c9a84c";
const SAGE    = "#5C7657";
const INK     = "#0d1b2a";
const MUTED   = "#64748b";
const HAIR    = "rgba(13,27,42,0.06)";

const SURFACE = {
  card:       "#ffffff",
  cardBorder: "1px solid #eef0f4",
  cardShadow: "0 1px 2px rgba(13,27,42,0.04), 0 4px 16px rgba(13,27,42,0.04)",
  cardRadius: 2,
} as const;

const FONT_SERIF = `'Fraunces', 'GT Sectra', 'Tiempos Headline', Georgia, serif`;
const FONT_MONO  = `'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace`;

/** Compact human duration: "9 days", "3h 14m", "47m", "Active" (when active). */
function formatDuration(seconds: number, isActive: boolean): string {
  if (isActive) return "Active";
  if (!Number.isFinite(seconds) || seconds <= 0) return "—";
  const days = Math.floor(seconds / 86400);
  if (days >= 2)  return `${days} days`;
  if (days === 1) return "1 day";
  const hours = Math.floor(seconds / 3600);
  if (hours >= 1) {
    const mins = Math.floor((seconds % 3600) / 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  const mins = Math.max(1, Math.floor(seconds / 60));
  return `${mins}m`;
}

function formatShortDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!isFinite(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
}

function staffName(row: AssignmentHistoryRow): string {
  const u = row.to_user;
  if (!u) return row.assigned_to ? `User #${row.assigned_to}` : "Unassigned";
  return (u as any).display_name
      || [(u as any).first_name, (u as any).last_name].filter(Boolean).join(" ")
      || (u as any).username
      || `User #${u.id}`;
}

function staffInitials(name: string): string {
  return name.split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

interface Props {
  leadId: number;
}

export default function AssignmentHistoryPanel({ leadId }: Props) {
  const [rows, setRows]       = useState<AssignmentHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAssignmentHistory(leadId);
      // Backend orders by assigned_at DESC — newest first. Keep that
      // (current owner pinned at top, oldest at bottom).
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.message ?? "Could not load assignment history");
    }
    setLoading(false);
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Paper elevation={0} sx={{ p: 2.2, ...SURFACE, border: SURFACE.cardBorder }}>
      {/* ── Section header ─────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
        <HistoryIcon sx={{ fontSize: 16, color: GOLD }} />
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, letterSpacing: 0.2 }}>
            Assignment History
          </Typography>
          <Typography sx={{ fontSize: 11, color: MUTED, mt: 0.1 }}>
            Every owner this lead has had · activity count per assignment
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ fontSize: 11.5, py: 0.4, borderRadius: 1.5, mb: 1 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={20} sx={{ color: GOLD }} />
        </Stack>
      ) : rows.length === 0 ? (
        <Box sx={{
          p: 2, borderRadius: 1.5, bgcolor: "#fafbfc",
          border: `1px dashed ${HAIR}`,
          textAlign: "center",
        }}>
          <Typography sx={{ fontSize: 11.5, color: MUTED }}>
            No assignment history recorded yet.
          </Typography>
        </Box>
      ) : (
        /* ── The table — Staff | Assigned | Removed | Duration | Acts ── */
        <Box sx={{
          borderRadius: 1.5,
          border: `1px solid ${HAIR}`,
          overflow: "hidden",
        }}>
          {/* Header row */}
          <Box sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.7fr) 0.9fr 0.9fr 0.8fr 0.6fr",
            gap: 1,
            px: 1.4, py: 0.8,
            bgcolor: "#fafbfc",
            borderBottom: `1px solid ${HAIR}`,
          }}>
            {["Staff", "Assigned", "Removed", "Duration", "Acts"].map((h) => (
              <Typography
                key={h}
                sx={{
                  fontFamily: FONT_MONO, fontSize: 9, fontWeight: 700,
                  color: MUTED, letterSpacing: "0.14em",
                  textTransform: "uppercase",
                }}
              >
                {h}
              </Typography>
            ))}
          </Box>

          {/* Data rows */}
          {rows.map((row, i) => {
            const name = staffName(row);
            const initials = staffInitials(name);
            const profileImg = (row.to_user as any)?.profile_image as string | null | undefined;
            const isLast = i === rows.length - 1;
            return (
              <Box
                key={row.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1.7fr) 0.9fr 0.9fr 0.8fr 0.6fr",
                  gap: 1,
                  px: 1.4, py: 1,
                  alignItems: "center",
                  bgcolor: row.is_active ? "rgba(92,118,87,0.04)" : "transparent",
                  borderBottom: isLast ? "none" : `1px solid ${HAIR}`,
                  transition: "background 120ms ease",
                  "&:hover": { bgcolor: row.is_active ? "rgba(92,118,87,0.07)" : "#fafbfc" },
                }}
              >
                {/* Staff (avatar + name + tiny "current" badge) */}
                <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                  <Avatar
                    src={profileImg || undefined}
                    sx={{
                      width: 26, height: 26,
                      bgcolor: row.is_active ? SAGE : "#cbd5e1",
                      fontSize: 10, fontWeight: 700,
                    }}
                  >
                    {initials || "—"}
                  </Avatar>
                  <Box minWidth={0}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 12, fontWeight: 700, color: INK,
                        textTransform: "capitalize",
                        lineHeight: 1.2,
                      }}
                    >
                      {name}
                    </Typography>
                    {row.is_active ? (
                      <Box sx={{
                        display: "inline-flex", alignItems: "center", gap: 0.4,
                        mt: 0.2, px: 0.6, py: 0.05,
                        borderRadius: "999px",
                        bgcolor: `${SAGE}18`, color: SAGE,
                        fontSize: 8.5, fontWeight: 800,
                        letterSpacing: "0.08em", textTransform: "uppercase",
                      }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: SAGE }} />
                        Current
                      </Box>
                    ) : (
                      row.reason && (
                        <Typography
                          noWrap
                          sx={{ fontSize: 9.5, color: MUTED, mt: 0.2, lineHeight: 1.2 }}
                          title={row.reason}
                        >
                          {row.reason}
                        </Typography>
                      )
                    )}
                  </Box>
                </Stack>

                {/* Assigned at */}
                <Typography sx={{
                  fontFamily: FONT_MONO, fontSize: 11, color: INK,
                  fontVariantNumeric: "tabular-nums",
                }}>
                  {formatShortDate(row.assigned_at)}
                </Typography>

                {/* Removed at — "Active" pill when current */}
                {row.is_active ? (
                  <Chip
                    label="Active"
                    size="small"
                    sx={{
                      height: 18, fontSize: 9, fontWeight: 800,
                      letterSpacing: "0.08em", textTransform: "uppercase",
                      bgcolor: `${SAGE}18`, color: SAGE,
                      borderRadius: "999px",
                    }}
                  />
                ) : (
                  <Typography sx={{
                    fontFamily: FONT_MONO, fontSize: 11, color: MUTED,
                    fontVariantNumeric: "tabular-nums",
                  }}>
                    {formatShortDate(row.removed_at)}
                  </Typography>
                )}

                {/* Duration */}
                <Typography sx={{
                  fontFamily: FONT_SERIF, fontSize: 12.5, fontWeight: 500,
                  color: row.is_active ? SAGE : INK,
                  letterSpacing: "-0.01em",
                  fontVariantNumeric: "tabular-nums",
                }}>
                  {formatDuration(row.duration_seconds, row.is_active)}
                </Typography>

                {/* Activity count */}
                <Typography sx={{
                  fontFamily: FONT_SERIF, fontSize: 14, fontWeight: 500,
                  color: row.activity_count > 0 ? NAVY : MUTED,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                }}>
                  {row.activity_count ?? 0}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
}
